/**
 * Text-message settings.
 *
 * Three states in one modal, because they are one decision: which number, prove
 * it, agree to be texted. The consent checkbox lives with the code rather than
 * with the number on purpose - consent given before the number is proved is
 * consent for whichever handset was actually typed.
 */
import { mountOtpCode } from '../shared/otp-code.mjs';

// How long "Send a new code" waits after each code. Only a pause for the eye:
// the limit that actually protects the texting bill is SmsSubscription's, on
// the server, and it answers 429 whatever this says.
const RESEND_COOLDOWN_SECONDS = 30;

function initSmsPreferences() {
  const modal = document.querySelector('[data-sms-preferences-modal]');
  if (!modal || !window.axios) return;

  const endpoints = {
    code: modal.dataset.smsCodeEndpoint,
    verify: modal.dataset.smsVerifyEndpoint,
    update: modal.dataset.smsUpdateEndpoint,
  };

  const panels = {
    subscribed: modal.querySelector('[data-sms-state="subscribed"]'),
    phone: modal.querySelector('[data-sms-state="phone"]'),
    code: modal.querySelector('[data-sms-state="code"]'),
  };

  const actionButton = modal.querySelector('[data-sms-action]');
  const actionLabel = modal.querySelector('[data-sms-action-label]');
  const spinner = modal.querySelector('[data-sms-spinner]');
  const status = modal.querySelector('[data-sms-status]');
  const phoneInput = modal.querySelector('[data-sms-phone]');
  const otpRoot = modal.querySelector('[data-sms-code]');
  const otp = mountOtpCode(otpRoot);
  const consentInput = modal.querySelector('[data-sms-consent]');
  const phoneEcho = modal.querySelector('[data-sms-phone-echo]');
  const resendButton = modal.querySelector('[data-sms-resend]');
  const changeNumberButton = modal.querySelector('[data-sms-change-number]');

  if (!actionButton || !actionLabel || !panels.phone || !panels.code || !otp) return;

  let state = modal.dataset.smsSubscribed === 'true' ? 'subscribed' : 'phone';

  const labels = {
    subscribed: 'Turn off text alerts',
    phone: 'Send code',
    code: 'Verify and turn on alerts',
  };

  function show(next) {
    state = next;

    Object.entries(panels).forEach(([name, panel]) => {
      panel?.classList.toggle('d-none', name !== next);
    });

    actionLabel.textContent = labels[next];
    modal.dataset.smsSubscribed = String(next === 'subscribed');
  }

  function report(message, ok) {
    if (status) status.textContent = message;
    if (ok) window.toastr?.success(message);
    else window.toastr?.error(message);
  }

  // Every request from this modal is one button press. One flag across both
  // buttons, not each button's own disabled state, is what stops a click on
  // "Send a new code" landing while "Send code" is still on its way - two texts.
  let pending = false;

  async function busy(work, { resend = false } = {}) {
    if (pending) return;

    pending = true;
    actionButton.disabled = true;
    actionButton.setAttribute('aria-busy', 'true');

    if (resend) {
      setResendLabel('Sending\u2026', true);
    } else {
      spinner?.classList.remove('d-none');
    }

    try {
      await work();
    } catch (error) {
      report(
        error?.response?.data?.message || 'That did not work. Please try again.',
        false
      );
    } finally {
      pending = false;
      actionButton.disabled = false;
      actionButton.setAttribute('aria-busy', 'false');
      spinner?.classList.add('d-none');
      renderResend();
    }
  }

  // "Send a new code" counts down after each code rather than staying live, so
  // the first reaction to a slow text is to wait for it, not to buy another.
  let cooldownUntil = 0;
  let cooldownTimer = null;

  function setResendLabel(text, disabled) {
    if (!resendButton) return;

    resendButton.textContent = text;
    resendButton.disabled = disabled;
  }

  function renderResend() {
    const left = Math.ceil((cooldownUntil - Date.now()) / 1000);

    if (left > 0) {
      setResendLabel(`Send a new code in 0:${String(left).padStart(2, '0')}`, true);
      return;
    }

    stopCooldown();
    setResendLabel('Send a new code', pending);
  }

  function startCooldown() {
    stopCooldown();
    cooldownUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    cooldownTimer = window.setInterval(renderResend, 1000);
    renderResend();
  }

  function stopCooldown() {
    if (cooldownTimer) window.clearInterval(cooldownTimer);
    cooldownTimer = null;
    cooldownUntil = 0;
  }

  async function requestCode() {
    const response = await window.axios.post(endpoints.code, { phone: phoneInput?.value });

    // The number the server will actually text, not the one that was typed:
    // "+14155550186:" parses as a number and used to be echoed colon and all.
    if (phoneEcho) phoneEcho.textContent = response.data.phone || phoneInput?.value || '';
    if (consentInput) consentInput.checked = false;

    otp.clear();
    show('code');
    startCooldown();
    otp.focus();
    report(response.data.message, true);
  }

  async function verify() {
    if (!otp.complete) {
      otp.setInvalid(true);
      otp.focus();
      report('Enter the 6-digit code we sent you.', false);
      return;
    }

    if (consentInput && !consentInput.checked) {
      consentInput.focus();
      report('Please agree to receive text messages first.', false);
      return;
    }

    let response;

    try {
      response = await window.axios.post(endpoints.verify, {
        code: otp.value,
        consent: true,
      });
    } catch (error) {
      // A wrong or expired code: empty the boxes and mark them, so the next
      // attempt starts clean and the red says which field was refused.
      if (error?.response?.status === 422) {
        otp.clear();
        otp.setInvalid(true);
        otp.focus();
      }

      throw error;
    }

    show('subscribed');
    report(response.data.message, true);
    // The number the server normalised, not the one that was typed.
    modal.querySelectorAll('[data-sms-phone-echo]').forEach((el) => {
      el.textContent = response.data.phone;
    });
  }

  async function unsubscribe() {
    const response = await window.axios.patch(endpoints.update, { sms_enabled: false });

    // The new state comes from the response rather than from what was sent, so
    // a refused change never leaves the modal showing something untrue.
    show(response.data.sms_enabled ? 'subscribed' : 'phone');
    report(response.data.message, true);
  }

  actionButton.addEventListener('click', () => busy(() => {
    if (state === 'subscribed') return unsubscribe();
    if (state === 'code') return verify();

    return requestCode();
  }));

  resendButton?.addEventListener('click', (event) => {
    event.preventDefault();
    busy(requestCode, { resend: true });
  });

  changeNumberButton?.addEventListener('click', () => {
    stopCooldown();
    renderResend();
    show('phone');
    phoneInput?.focus();
    phoneInput?.select();
  });

  // Enter does what the footer button does on either step. Completing the code
  // moves nothing: the consent is a separate, deliberate choice.
  otpRoot.addEventListener('otp:submit', () => {
    if (state === 'code') actionButton.click();
  });

  phoneInput?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || state !== 'phone') return;

    event.preventDefault();
    actionButton.click();
  });

  show(state);
}

document.addEventListener('DOMContentLoaded', initSmsPreferences);
